import React from 'react';

// Base skeleton pulse element
const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-muted rounded ${className}`} />
);

// ── Staggered fade-slide wrapper ─────────────────────────────────────────────
// Wraps page content so each direct child fades + slides up with a stagger delay.
// Usage: <FadeSlideIn><Section1 /><Section2 /></FadeSlideIn>
export const FadeSlideIn = ({ children, className = '' }) => (
  <div className={className}>
    {React.Children?.map(children, (child, i) => (
      <div
        key={i}
        className="animate-fade-slide-up"
        style={{ animationDelay: `${i * 60}ms`, animationFillMode: 'both' }}
      >
        {child}
      </div>
    ))}
  </div>
);

// KPI Card skeleton for dashboard
export const KPICardSkeleton = () => (
  <div className="bg-card border border-border rounded-xl p-4 sm:p-5 space-y-3">
    <div className="flex items-center justify-between">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-8 w-8 rounded-lg" />
    </div>
    <Skeleton className="h-8 w-20" />
    <Skeleton className="h-3 w-32" />
  </div>
);

// Analytics chart skeleton
export const ChartSkeleton = ({ height = 'h-48' }) => (
  <div className="bg-card border border-border rounded-xl p-4 sm:p-5 space-y-3">
    <div className="flex items-center justify-between mb-2">
      <Skeleton className="h-5 w-36" />
      <Skeleton className="h-7 w-24 rounded-lg" />
    </div>
    <div className={`${height} w-full flex items-end gap-2 pt-4`}>
      {[65, 40, 80, 55, 90, 45, 70, 60, 85, 50, 75, 65]?.map((h, i) => (
        <div key={i} className="flex-1 flex flex-col justify-end">
          <Skeleton className="w-full rounded-t" style={{ height: `${h}%` }} />
        </div>
      ))}
    </div>
    <div className="flex justify-between">
      {[...Array(6)]?.map((_, i) => (
        <Skeleton key={i} className="h-3 w-8" />
      ))}
    </div>
  </div>
);

// Admin data table skeleton
export const TableSkeleton = ({ rows = 5, cols = 5 }) => (
  <div className="bg-card border border-border rounded-xl overflow-hidden">
    {/* Table header */}
    <div className="border-b border-border px-4 py-3 flex items-center justify-between">
      <Skeleton className="h-5 w-32" />
      <div className="flex gap-2">
        <Skeleton className="h-8 w-24 rounded-lg" />
        <Skeleton className="h-8 w-20 rounded-lg" />
      </div>
    </div>
    {/* Column headers */}
    <div className="grid px-4 py-2 border-b border-border" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {[...Array(cols)]?.map((_, i) => (
        <Skeleton key={i} className="h-3 w-16" />
      ))}
    </div>
    {/* Rows */}
    {[...Array(rows)]?.map((_, rowIdx) => (
      <div
        key={rowIdx}
        className="grid px-4 py-3 border-b border-border last:border-0 items-center"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
      >
        {[...Array(cols)]?.map((_, colIdx) => (
          <Skeleton
            key={colIdx}
            className="h-4"
            style={{ width: colIdx === 0 ? '80%' : colIdx === cols - 1 ? '60%' : '70%' }}
          />
        ))}
      </div>
    ))}
  </div>
);

// Bible verse list skeleton
export const VerseListSkeleton = ({ count = 8 }) => (
  <div className="space-y-1">
    {[...Array(count)]?.map((_, i) => (
      <div key={i} className="flex gap-3 py-2 px-1 items-start">
        <Skeleton className="h-4 w-6 mt-0.5 shrink-0 rounded" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-4 w-full" />
          {i % 3 !== 2 && <Skeleton className="h-4 w-4/5" />}
        </div>
      </div>
    ))}
  </div>
);

// Dashboard progress summary skeleton
export const ProgressSummarySkeleton = () => (
  <div className="bg-card border border-border rounded-xl p-4 sm:p-5 space-y-4">
    <Skeleton className="h-5 w-40" />
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[...Array(4)]?.map((_, i) => (
        <div key={i} className="space-y-2 text-center">
          <Skeleton className="h-8 w-12 mx-auto" />
          <Skeleton className="h-3 w-16 mx-auto" />
        </div>
      ))}
    </div>
    <Skeleton className="h-2 w-full rounded-full" />
  </div>
);

// EGW panel skeleton — matches the amber-tinted accordion layout
export const EGWPanelSkeleton = () => (
  <div className="mt-3 bg-amber-500/5 border border-amber-500/20 rounded-lg overflow-hidden animate-pulse">
    {/* Header */}
    <div className="flex items-center justify-between px-4 py-3 bg-amber-500/10 border-b border-amber-500/20">
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-4 rounded" />
        <Skeleton className="h-4 w-28" />
      </div>
      <Skeleton className="h-5 w-5 rounded" />
    </div>
    <div className="p-3 space-y-2">
      {/* Accordion row 1 */}
      <div className="bg-amber-500/10 rounded-lg p-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-1 rounded" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-5 w-6 rounded" />
        </div>
        <Skeleton className="h-3 w-3 rounded" />
      </div>
      {/* Accordion row 2 */}
      <div className="bg-amber-500/10 rounded-lg p-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-1 rounded" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-5 w-6 rounded" />
        </div>
        <Skeleton className="h-3 w-3 rounded" />
      </div>
    </div>
  </div>
);

// Commentaries panel skeleton — matches the commentator pills + content layout
export const CommentaryPanelSkeleton = () => (
  <div className="mt-3 bg-amber-500/5 border border-amber-500/20 rounded-lg overflow-hidden animate-pulse">
    {/* Header */}
    <div className="flex items-center justify-between px-4 py-3 bg-amber-500/10">
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-4 rounded" />
        <Skeleton className="h-4 w-24" />
      </div>
      <Skeleton className="h-5 w-5 rounded" />
    </div>
    <div className="p-3 space-y-3">
      {/* Commentator pills */}
      <div className="flex flex-wrap gap-1.5">
        {[80, 100, 70, 90, 85, 75, 110, 95]?.map((w, i) => (
          <Skeleton key={i} className="h-7 rounded-md" style={{ width: `${w}px` }} />
        ))}
      </div>
      {/* Content area */}
      <div className="bg-card border border-border rounded-lg p-3 space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </div>
  </div>
);

// SDA Commentary panel skeleton
export const SDACommentaryPanelSkeleton = () => (
  <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg animate-pulse">
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-36" />
        </div>
        <Skeleton className="h-6 w-6 rounded" />
      </div>
      {/* Content lines */}
      <div className="space-y-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </div>
  </div>
);

// ── Page-level skeleton layouts ───────────────────────────────────────────────

// Dashboard page skeleton
export const DashboardSkeleton = () => (
  <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-24 md:pb-6 space-y-6">
    {/* Avatar + welcome row */}
    <div className="flex items-center gap-4">
      <Skeleton className="w-16 h-16 sm:w-20 sm:h-20 rounded-full shrink-0" />
      <div className="space-y-2 flex-1">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
    {/* KPI cards */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
      {[...Array(4)]?.map((_, i) => <KPICardSkeleton key={i} />)}
    </div>
    {/* Progress summary */}
    <ProgressSummarySkeleton />
    {/* Two-column lower section */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
      <div className="lg:col-span-2 space-y-4">
        <Skeleton className="h-40 rounded-xl" />
        <Skeleton className="h-32 rounded-xl" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-36 rounded-xl" />
        <Skeleton className="h-28 rounded-xl" />
      </div>
    </div>
  </div>
);

// Verse Library page skeleton
export const VerseLibrarySkeleton = () => (
  <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-24 md:pb-6 space-y-5">
    {/* Header row */}
    <div className="flex items-center justify-between">
      <Skeleton className="h-8 w-40" />
      <div className="flex gap-2">
        <Skeleton className="h-9 w-24 rounded-lg" />
        <Skeleton className="h-9 w-24 rounded-lg" />
      </div>
    </div>
    {/* Search + filter bar */}
    <div className="flex gap-3">
      <Skeleton className="h-10 flex-1 rounded-lg" />
      <Skeleton className="h-10 w-28 rounded-lg" />
    </div>
    {/* Stats row */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[...Array(4)]?.map((_, i) => (
        <div key={i} className="bg-card border border-border rounded-xl p-3 space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-7 w-12" />
        </div>
      ))}
    </div>
    {/* Verse cards */}
    <div className="space-y-3">
      {[...Array(6)]?.map((_, i) => (
        <div key={i} className="bg-card border border-border rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-6 w-16 rounded-full" />
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
          <div className="flex gap-2 pt-1">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Daily Review page skeleton
export const DailyReviewSkeleton = () => (
  <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-5">
    {/* Header stats */}
    <div className="grid grid-cols-3 gap-3">
      {[...Array(3)]?.map((_, i) => (
        <div key={i} className="bg-card border border-border rounded-xl p-3 space-y-1.5 text-center">
          <Skeleton className="h-7 w-10 mx-auto" />
          <Skeleton className="h-3 w-16 mx-auto" />
        </div>
      ))}
    </div>
    {/* Main review card */}
    <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-4/5" />
      <Skeleton className="h-4 w-3/4" />
      <div className="pt-2">
        <Skeleton className="h-10 w-full rounded-xl" />
      </div>
    </div>
    {/* Response buttons */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[...Array(4)]?.map((_, i) => (
        <Skeleton key={i} className="h-12 rounded-xl" />
      ))}
    </div>
  </div>
);

// Bible Reading Plans page skeleton
export const BibleReadingPlansSkeleton = () => (
  <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-24 md:pb-6 space-y-6">
    {/* Tab bar */}
    <div className="flex gap-2">
      <Skeleton className="h-9 w-28 rounded-lg" />
      <Skeleton className="h-9 w-28 rounded-lg" />
    </div>
    {/* Search + filter */}
    <div className="flex gap-3">
      <Skeleton className="h-10 flex-1 rounded-lg" />
      <Skeleton className="h-10 w-32 rounded-lg" />
    </div>
    {/* Active plans */}
    <div className="space-y-3">
      <Skeleton className="h-5 w-36" />
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {[...Array(2)]?.map((_, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-4 space-y-3">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-2 w-full rounded-full" />
            <div className="flex gap-2">
              <Skeleton className="h-8 flex-1 rounded-lg" />
              <Skeleton className="h-8 flex-1 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    </div>
    {/* Plan category cards */}
    <div className="space-y-3">
      <Skeleton className="h-5 w-44" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)]?.map((_, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-4 space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-8 w-full rounded-lg mt-2" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Memorization Practice page skeleton
export const MemorizationPracticeSkeleton = () => (
  <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-24 md:pb-6 space-y-5">
    {/* Mode selector */}
    <div className="bg-card border border-border rounded-xl p-4 space-y-3">
      <Skeleton className="h-5 w-36" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[...Array(4)]?.map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
    </div>
    {/* Verse display area */}
    <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-6 w-16 rounded-full" />
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-4/5" />
      <Skeleton className="h-10 w-full rounded-xl mt-2" />
    </div>
    {/* Session stats */}
    <div className="grid grid-cols-3 gap-3">
      {[...Array(3)]?.map((_, i) => (
        <div key={i} className="bg-card border border-border rounded-xl p-3 text-center space-y-1">
          <Skeleton className="h-6 w-10 mx-auto" />
          <Skeleton className="h-3 w-14 mx-auto" />
        </div>
      ))}
    </div>
  </div>
);

// User Profile page skeleton
export const UserProfileSkeleton = () => (
  <div className="p-4 max-w-6xl mx-auto space-y-6">
    {/* Profile header card */}
    <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
      <div className="flex items-center gap-4">
        <Skeleton className="h-16 w-16 rounded-full shrink-0" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-28 rounded-xl" />
      </div>
    </div>
    {/* Tab bar */}
    <div className="bg-card border border-border rounded-2xl p-2">
      <div className="flex flex-wrap gap-1">
        {[...Array(6)]?.map((_, i) => (
          <Skeleton key={i} className="h-9 w-24 rounded-lg" />
        ))}
      </div>
    </div>
    {/* Tab content */}
    <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
      <Skeleton className="h-5 w-32" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[...Array(4)]?.map((_, i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Study Notes page skeleton
export const StudyNotesSkeleton = () => (
  <div className="max-w-5xl mx-auto px-4 py-8 pb-24 md:pb-8 space-y-6">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="space-y-1.5">
        <Skeleton className="h-7 w-36" />
        <Skeleton className="h-4 w-56" />
      </div>
      <Skeleton className="h-9 w-36 rounded-lg" />
    </div>
    {/* Note cards grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[...Array(4)]?.map((_, i) => (
        <div key={i} className="bg-card border border-border rounded-xl p-5 space-y-3">
          <div className="flex items-start justify-between">
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full ml-2" />
          </div>
          <div className="flex gap-2 pt-1">
            <Skeleton className="h-7 w-20 rounded-lg" />
            <Skeleton className="h-7 w-20 rounded-lg" />
            <Skeleton className="h-7 w-20 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Blog page skeleton
export const BlogSkeleton = () => (
  <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
    {/* Search bar */}
    <Skeleton className="h-10 w-full max-w-md rounded-lg" />
    {/* Post grid */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {[...Array(6)]?.map((_, i) => (
        <div key={i} className="bg-card border border-border rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-4 w-20" />
          </div>
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-4/5" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <div className="flex gap-1 pt-1">
            <Skeleton className="h-5 w-12 rounded" />
            <Skeleton className="h-5 w-14 rounded" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Study Analytics page skeleton
export const StudyAnalyticsSkeleton = () => (
  <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-24 md:pb-6 space-y-6">
    {/* KPI row */}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      {[...Array(4)]?.map((_, i) => <KPICardSkeleton key={i} />)}
    </div>
    {/* Tab bar */}
    <div className="bg-card border border-border rounded-lg">
      <div className="flex gap-6 px-6 py-1 border-b border-border">
        {[...Array(4)]?.map((_, i) => (
          <Skeleton key={i} className="h-10 w-20" />
        ))}
      </div>
    </div>
    {/* Charts */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ChartSkeleton height="h-56" />
      <ChartSkeleton height="h-56" />
    </div>
    <ChartSkeleton height="h-40" />
  </div>
);

export default Skeleton;
